<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-calculator</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Presupuestos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Presupuesto vs real · ingresos CxC · gastos CxP
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Presupuesto</div>
          <div class="text-h5 font-weight-black">{{ kpis.budget_total | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Real</div>
          <div class="text-h5 font-weight-black">{{ kpis.actual_total | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Variación</div>
          <div class="text-h5 font-weight-black" :class="(kpis.variance_total || 0) >= 0 ? 'success--text' : 'error--text'">
            {{ kpis.variance_total | moneda }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Líneas</div>
          <div class="text-h5 font-weight-black">{{ kpis.line_count || 0 }}</div>
          <div class="caption grey--text">Sobre {{ kpis.over_count || 0 }} · Bajo {{ kpis.under_count || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <v-text-field v-model.number="year" type="number" outlined dense hide-details label="Año" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="month" :items="monthItems" item-text="text" item-value="value" outlined dense hide-details label="Mes" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="costCenterId" :items="centerItems" item-text="text" item-value="value" outlined dense hide-details clearable label="Centro" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="4" class="text-right">
          <v-btn color="primary" class="font-weight-bold" @click="openLine">Nueva línea</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card mx-4">
      <v-data-table :headers="headers" :items="items" :loading="loading" hide-default-footer class="elevation-0" no-data-text="Cargue líneas de presupuesto.">
        <template v-slot:item.amount="{ item }">{{ item.amount | moneda }}</template>
        <template v-slot:item.actual="{ item }">{{ item.actual | moneda }}</template>
        <template v-slot:item.variance="{ item }">
          <span :class="item.status === 'over' ? 'error--text' : (item.status === 'under' ? 'warning--text' : 'success--text')">
            {{ item.variance | moneda }}
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small>{{ statusLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openEdit(item)"><v-icon small>fas fa-pen</v-icon></v-btn>
          <v-btn icon small @click="remove(item)"><v-icon small>fas fa-trash</v-icon></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="480">
      <v-card>
        <v-card-title class="font-weight-black">{{ form.id ? 'Editar línea' : 'Nueva línea' }}</v-card-title>
        <v-card-text>
          <v-select v-model="form.category_id" :items="categoryItems" item-text="text" item-value="value" label="Categoría" outlined dense />
          <v-select v-model="form.cost_center_id" :items="centerItems" item-text="text" item-value="value" label="Centro (opcional)" outlined dense clearable />
          <v-text-field v-model="form.amount" type="number" label="Monto" outlined dense />
          <v-text-field v-model="form.notes" label="Notas" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  deleteBudgetLine,
  fetchBudgetCompare,
  fetchCostCenters,
  fetchFinanceCategories,
  upsertBudgetLine,
} from '@/api/finance'

const MONTHS = [
  { value: 1, text: 'Enero' }, { value: 2, text: 'Febrero' }, { value: 3, text: 'Marzo' },
  { value: 4, text: 'Abril' }, { value: 5, text: 'Mayo' }, { value: 6, text: 'Junio' },
  { value: 7, text: 'Julio' }, { value: 8, text: 'Agosto' }, { value: 9, text: 'Septiembre' },
  { value: 10, text: 'Octubre' }, { value: 11, text: 'Noviembre' }, { value: 12, text: 'Diciembre' },
]

export default {
  name: 'FinanceBudgets',
  data() {
    const now = new Date()
    return {
      loading: false,
      saving: false,
      dialog: false,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      costCenterId: null,
      kpis: {},
      items: [],
      categories: [],
      centers: [],
      form: { category_id: null, cost_center_id: null, amount: '', notes: '' },
      monthItems: MONTHS,
      headers: [
        { text: 'Categoría', value: 'category_name' },
        { text: 'Tipo', value: 'category_kind' },
        { text: 'Centro', value: 'cost_center_code' },
        { text: 'Presupuesto', value: 'amount', align: 'end' },
        { text: 'Real', value: 'actual', align: 'end' },
        { text: 'Variación', value: 'variance', align: 'end' },
        { text: '', value: 'status' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 88 },
      ],
    }
  },
  computed: {
    categoryItems() {
      return this.categories.map(c => ({ value: c.id, text: `${c.code} · ${c.name}` }))
    },
    centerItems() {
      return this.centers.map(c => ({ value: c.id, text: `${c.code} · ${c.name}` }))
    },
  },
  methods: {
    statusLabel(status) {
      if (status === 'over') return 'Sobre'
      if (status === 'under') return 'Bajo'
      return 'En meta'
    },
    async fetchAll() {
      this.loading = true
      try {
        const [compare, cats, centers] = await Promise.all([
          fetchBudgetCompare({ year: this.year, month: this.month, cost_center_id: this.costCenterId || undefined }),
          fetchFinanceCategories(),
          fetchCostCenters(),
        ])
        this.kpis = compare.kpis || {}
        this.items = compare.items || []
        this.categories = cats.items || []
        this.centers = centers.items || []
      } catch (e) {
        notifications.showError('No se pudo cargar el presupuesto')
      } finally {
        this.loading = false
      }
    },
    openLine() {
      this.form = {
        category_id: (this.categories[0] && this.categories[0].id) || null,
        cost_center_id: this.costCenterId,
        amount: '',
        notes: '',
      }
      this.dialog = true
    },
    openEdit(item) {
      this.form = {
        category_id: item.category_id,
        cost_center_id: item.cost_center_id,
        amount: item.amount,
        notes: item.notes || '',
      }
      this.dialog = true
    },
    async submit() {
      this.saving = true
      try {
        await upsertBudgetLine({
          year: this.year,
          month: this.month,
          category_id: this.form.category_id,
          cost_center_id: this.form.cost_center_id || null,
          amount: this.form.amount,
          notes: this.form.notes || null,
        })
        notifications.showSuccess('Línea guardada')
        this.dialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo guardar')
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      if (!confirm('¿Eliminar esta línea de presupuesto?')) return
      try {
        await deleteBudgetLine(item.id)
        notifications.showSuccess('Línea eliminada')
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo eliminar')
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
